<template>
    <pc-loader
        v-bind:loading="showLoader"
        v-bind:message="loadMessage">
        <div v-if="!loading">
            <project-locked-banner v-if="!isAdmin" />
            <pc-alert v-bind:shown="isAutoPromotedForm">
                {{ autoPromotionMessage }}
            </pc-alert>
            <div
                v-if="displayPrintHeader"
                class="d-none d-print-block">
                {{ printHeader }}
            </div>
            <div
                id="ProjectFormHeader"
                v-bind:class="titleClasses"
                data-test-role="form-project">
                <div class="d-flex justify-content-between flex-wrap">
                    <div class="order-md-2 d-flex align-self-baseline">
                        <div class="order-md-2">
                            <slot name="header-project-number">
                                <h2
                                    v-if="Project.ProjectNumber"
                                    data-test-role="form-project-num">
                                    {{ projectNumberLabelText }}
                                </h2>
                            </slot>
                        </div>
                        <div
                            v-if="trackChanges && hasSeenChanges"
                            data-test-role="project-save-state"
                            class="mx-2 align-self-center order-md-1">
                            <span
                                v-if="hasUnsavedChanges || isSaving"
                                key="saving"
                                data-test-role="save-state-autosaving">
                                <span class="fst-italic"> {{ savingText }} </span>
                            </span>
                            <span
                                v-else-if="hasSavedChanges"
                                key="saved"
                                data-test-role="save-state-saved">
                                <span class="fst-italic"> {{ savedText }} </span>
                                <span class="text-success">&#x2714;</span>
                            </span>
                        </div>
                    </div>
                    <form-title-subtitle
                        class="order-md-1"
                        v-bind:title="Form.Title"
                        v-bind:subtitle="Form.Subtitle" />
                </div>
                <slot name="header-bottom" />
            </div>
            <div
                v-if="showPages"
                class="mb-3 py-3 border-bottom">
                <pagination-tabs
                    v-bind:pages="Pages"
                    v-bind:current-page-number="currentPageNumber"
                    v-on:page-clicked="pageClicked" />
            </div>
            <div
                style="overflow: hidden"
                class="p-1">
                <div v-if="!isPrinterFriendly">
                    <form-page-transition
                        v-bind:page="currentPage"
                        v-bind:page-slide-left="pageSlideLeft"
                        v-bind:scroll-to-top-override="scrollToTopOverride"
                        v-on:page-transition-start="pageTransitionStart"
                        v-on:page-transition-end="pageTransitionEnd">
                        <component
                            v-bind="pageBinder"
                            v-bind:is="pageComponent"
                            v-bind:key="currentPage.PageNumber"
                            v-bind:page="currentPage" />
                    </form-page-transition>
                </div>
                <div v-else>
                    <div
                        v-for="(page, index) in Pages"
                        v-bind:key="page.id">
                        <component
                            v-bind="pageBinder"
                            v-bind:is="pageComponent"
                            v-bind:key="pageAtIndex(index).PageNumber"
                            v-bind:page="pageAtIndex(index)"
                            printer-friendly />
                    </div>
                </div>
                <validation-errors
                    v-if="hasValidationErrors"
                    v-bind:header="unableToSubmitHeading"
                    v-bind:subheader="unableToSubmitSubHeading"
                    v-bind:groups="validationErrorGroups"
                    v-on:error-link="setElement($event.error.FormElementPublicId)" />
                <div v-if="!isPrinterFriendly">
                    <div class="border-top pt-3">
                        <div class="row">
                            <div class="col d-flex justify-content-start">
                                <pc-transition>
                                    <pc-button
                                        v-if="showPrev"
                                        id="BackButton"
                                        v-on:click="previousPage">
                                        {{ backButtonLabel }}
                                    </pc-button>
                                </pc-transition>
                            </div>
                            <div class="col d-flex justify-content-center">
                                <pc-transition>
                                    <div v-if="showSubmit">
                                        <pc-button
                                            id="SubmitButton"
                                            data-gtmid="EditProject-SubmitProject"
                                            primary
                                            v-bind:disabled="disableSubmit"
                                            v-on:click="submit">
                                            {{ submitText }}
                                        </pc-button>
                                        <pc-help-icon
                                            v-if="disableSubmit"
                                            class="ms-2"
                                            v-on:click="showEPaymentSubmitHelp = !showEPaymentSubmitHelp" />
                                    </div>
                                </pc-transition>
                            </div>
                            <div class="col d-flex justify-content-end">
                                <pc-transition>
                                    <pc-button
                                        v-if="showNext"
                                        id="NextButton"
                                        v-on:click="nextPage">
                                        {{ nextText }}
                                    </pc-button>
                                </pc-transition>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-12 mt-2">
                                <pc-alert
                                    v-if="showSubmit && disableSubmit"
                                    v-model:shown="showEPaymentSubmitHelp"
                                    v-bind:message="ePaymentSubmitDisabledHelp"
                                    dismissible />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </pc-loader>
</template>
<script>
    import formPageTransition from "./formPageTransition.vue";
    import formPage from "./formPage.vue";
    import formTitleSubtitle from "./formTitleSubtitle.vue";
    import { PROJECT_NAMESPACE, createProjectModule } from "SharedStores/Project/projectStore";
    import { FORM_NAMESPACE, createFormModule, FORM_TYPES } from "SharedStores/Form/formStore";
    import {
        PROJECT_FORM_NAMESPACE,
        VALIDATION_ERRORS_MUTATION,
        createProjectFormModule,
        PROJECT_FORM_MODE,
        PROJECT_FORM_SAVE_STATE
    } from "./projectFormStore";
    import { PROJECT_FORM_CONTROLLER_NAMESPACE, PROJECT_FORM_CONTROLLER_TYPES } from "./projectFormControllerStore";
    import { USER_NAMESPACE, createUserModule } from "SharedStores/User/userStore";
    import { PROGRAM_NAMESPACE, createProgramModule } from "SharedStores/Program/programStore";
    import { isModuleRegistered } from "SharedStores/util";
    import { getAuxProjectElementState } from "./ProjectElements/projectElement";
    import formulaController from "Formula/calculatedFieldControllerMixin";
    import { FORM_VAL_ERR_TYPE } from "../../formValidationType";
    import { visibilityManagerMixin } from "./conditionalVisibility";
    import { pvStorageVisibilityManager } from "./pvStorageVisibility";
    import { isPageComplete } from "./ProjectElements/isComplete";
    import paginationTabs from "./paginationTabs.vue";
    import ePaymentPage from "./ePaymentPage.vue";
    import projectLockedBanner from "Projects/projectLockedBanner.vue";
    import validationErrors from "Shared/Formatting/validationErrors.vue";
    import { mapState } from "vuex";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/projectform.i18n";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";
    import { formatBasicUserInfoForDisplay } from "Util/clientUser";
    import { IS_DEBUG_BUILD, scrollToElement } from "Util/util";
    import { onDependencyUpdate } from "./ProjectElements/projectElement";
    import { useProjectLockStore } from "Projects/projectLockStore.js";

    export default {
        components: {
            formPageTransition,
            paginationTabs,
            projectLockedBanner,
            validationErrors,
            formTitleSubtitle
        },
        mixins: [formulaController, visibilityManagerMixin],
        provide() {
            return { projectForm: this };
        },
        inject: ["baseController"],
        props: {
            currentDate: { type: String, required: false, default: "" },
            currentTime: { type: String, required: false, default: "" },
            displayPrintHeader: { type: Boolean, required: false, default: false },
            trackChanges: { type: Boolean, required: false, default: false },
            beforeSubmit: { type: Function, required: false, default: null },
            isAutoPromotedForm: { type: Boolean, required: false, default: false },
            forceOnePage: { type: Boolean, required: false, default: false },
            hideEPaymentPage: { type: Boolean, required: false, default: false },
            disableEPaymentSubmit: { type: Boolean, required: false, default: false },
            scrollToTopOverride: { type: Function, required: false, default: null }
        },
        emits: ["submit-success"],
        setup() {
            const projectLockStore = useProjectLockStore();
            const pvStorageVisibility = pvStorageVisibilityManager();
            return { projectLockStore, pvStorageVisibility };
        },
        data() {
            return {
                currentPageNumber: 1,
                loading: true,
                reloading: false,
                pageSlideLeft: true,
                pageTransitionPromise: Promise.resolve(),
                showSubmitOnEPaymentPage: false,
                showEPaymentSubmitHelp: false
            };
        },
        computed: {
            titleClasses() {
                return "pb-2" + (this.isPrinterFriendly ? "" : " border-bottom");
            },
            userLabel() {
                return formatBasicUserInfoForDisplay(this.$app.LoggedInUser, this.$app.LoggedInRole);
            },
            currentPage() {
                return this.Pages[this.currentPageNumber - 1];
            },
            showNext() {
                return this.currentPageNumber < this.Pages.length;
            },
            nextText() {
                if (this.showEPaymentPage && this.currentPageNumber === this.Pages.length - 1) {
                    return this.proceedToPaymentLabel;
                }
                return this.nextButtonLabel;
            },
            showPrev() {
                return this.currentPageNumber > 1;
            },
            showSubmit() {
                return (
                    !this.isView &&
                    !this.isPrefill &&
                    !this.projectLockStore.isLocked &&
                    this.currentPageNumber === this.Pages.length &&
                    (!this.showEPaymentPage || this.showSubmitOnEPaymentPage)
                );
            },
            submitText() {
                if (this.isAdmin) {
                    return this.saveFormDataLabel;
                }
                if (this.isPreview) {
                    return this.closePreviewButtonLabel;
                }
                return this.submitButtonLabel;
            },
            projectFormMode() {
                return this.$store.state[PROJECT_FORM_NAMESPACE].ProjectFormMode;
            },
            validationErrors() {
                return this.$store.state[PROJECT_FORM_NAMESPACE].validationErrors;
            },
            saveState() {
                return this.$store.state[PROJECT_FORM_NAMESPACE].saveState;
            },
            hasSeenChanges() {
                return this.saveState !== PROJECT_FORM_SAVE_STATE.Initial;
            },
            hasUnsavedChanges() {
                return this.saveState === PROJECT_FORM_SAVE_STATE.HasUnsaved;
            },
            isSaving() {
                return this.saveState === PROJECT_FORM_SAVE_STATE.Saving;
            },
            hasSavedChanges() {
                return this.saveState === PROJECT_FORM_SAVE_STATE.Saved;
            },
            showLoader() {
                return this.loading || this.reloading;
            },
            hasValidationErrors() {
                return this.validationErrors && this.validationErrors.length > 0;
            },
            isAdmin() {
                return (
                    this.projectFormMode === PROJECT_FORM_MODE.Admin ||
                    this.projectFormMode === PROJECT_FORM_MODE.AdminReadonly
                );
            },
            isView() {
                return (
                    this.projectFormMode === PROJECT_FORM_MODE.View ||
                    this.projectFormMode === PROJECT_FORM_MODE.Print ||
                    this.projectFormMode === PROJECT_FORM_MODE.AdminReadonly
                );
            },
            isPreview() {
                return this.projectFormMode === PROJECT_FORM_MODE.Preview;
            },
            isPrefill() {
                return (
                    this.projectFormMode === PROJECT_FORM_MODE.Prefill ||
                    this.projectFormMode === PROJECT_FORM_MODE.PrefillAdmin
                );
            },
            isPrinterFriendly() {
                return this.$store.state[PROJECT_FORM_NAMESPACE].ProjectFormMode === PROJECT_FORM_MODE.Print;
            },
            projectIsNew() {
                return this.$store.state[PROJECT_NAMESPACE].IsNewProject;
            },
            showPages() {
                const minPages = this.showEPaymentPage ? 2 : 1;
                return this.Pages.length > minPages && !this.isPrinterFriendly;
            },
            missingFields() {
                let missingFields = this.getValidationErrorsOfType(FORM_VAL_ERR_TYPE.ElementMissing);
                //remove duplicates if we got both a missing field and invalid field message
                if (missingFields && this.invalidFields && this.invalidFields.length > 0) {
                    missingFields = missingFields.filter((mf) =>
                        this.invalidFields.every((inv) => inv.FormElementPublicId !== mf.FormElementPublicId)
                    );
                    if (missingFields.length === 0) {
                        missingFields = null;
                    }
                }
                return missingFields;
            },
            invalidFields() {
                return this.getValidationErrorsOfType(FORM_VAL_ERR_TYPE.ElementInvalid);
            },
            automationErrors() {
                return this.getValidationErrorsOfType(FORM_VAL_ERR_TYPE.AutomationFailure);
            },
            validationErrorGroups() {
                const groups = [];
                if (this.missingFields) {
                    groups.push({
                        heading: this.missingFieldsHeading,
                        testRole: "missing-fields",
                        keyProp: "FormElementPublicId",
                        errors: this.missingFields.map((err) => {
                            return {
                                ...err,
                                buttonLabel: `${this.pageLabel} ${this.elementIdToPageNumber[err.FormElementPublicId]}`
                            };
                        })
                    });
                }
                if (this.invalidFields) {
                    groups.push({
                        heading: this.invalidFormInputsHeading,
                        testRole: "invalid-fields",
                        keyProp: "FormElementPublicId",
                        primaryProp: "Label",
                        secondaryProp: "Description",
                        errors: this.invalidFields.map((err) => {
                            return {
                                ...err,
                                buttonLabel: `${this.pageLabel} ${this.elementIdToPageNumber[err.FormElementPublicId]}`
                            };
                        })
                    });
                }
                if (this.automationErrors) {
                    groups.push({
                        heading: this.automationFailuresHeading,
                        testRole: "automation-errors",
                        descriptionOnly: true,
                        errors: this.automationErrors
                    });
                }
                return groups;
            },
            elementIdToPageNumber() {
                const result = {};
                this.Pages.forEach((page) => {
                    if (page.FormElements) {
                        //not all pages have form elements (ie epayment page)
                        page.FormElements.forEach((elementId) => {
                            result[elementId] = page.PageNumber;
                        });
                    }
                });
                return result;
            },
            disableSubmit() {
                return this.disableEPaymentSubmit && this.$store.state[PROJECT_FORM_NAMESPACE].UseEPayments;
            },
            showEPaymentPage() {
                return !this.hideEPaymentPage && this.$store.state[PROJECT_FORM_NAMESPACE].UseEPayments;
            },
            showingEPaymentPage() {
                return this.showEPaymentPage && this.currentPageNumber === this.Pages.length;
            },
            pageComponent() {
                if (this.showingEPaymentPage) {
                    return ePaymentPage;
                }
                return formPage;
            },
            pageBinder() {
                if (this.showingEPaymentPage) {
                    const programId = this.$app.ProgramId;
                    const projectId = this.$store.state[PROJECT_NAMESPACE].ProjectId;
                    const formVersionId = this.$store.state[FORM_NAMESPACE].FormVersionPublicId;
                    const useDraftStorage = this.$store.state[PROJECT_NAMESPACE].UseDraftStorage;
                    const formMode = this.$store.state[PROJECT_FORM_NAMESPACE].ProjectFormMode;
                    const isPreview = formMode === PROJECT_FORM_MODE.Preview;
                    const isReadOnly = this.isView;
                    const isAdmin = this.isAdmin;

                    return {
                        programId,
                        projectId,
                        formVersionId,
                        useDraftStorage,
                        isPreview,
                        isReadOnly,
                        isAdmin
                    };
                }
                return {
                    page: this.currentPage
                };
            },
            Pages() {
                const pages = this.$store.getters[FORM_TYPES.getters.calculateFormPages]({
                    forceOnePage: this.forceOnePage
                });
                if (this.showEPaymentPage) {
                    pages.push({
                        isPaymentPage: true,
                        Title: this.ePaymentTitle,
                        PageNumber: pages.length + 1
                    });
                }
                return pages;
            },
            loadMessage() {
                if (this.isAutoPromotedForm) {
                    return this.autoPromotionLoadMessage;
                }

                return null;
            },
            ...mapState({
                Project: (state) => state[PROJECT_NAMESPACE],
                Form: (state) => state[FORM_NAMESPACE]
            }),
            ...mapI18n(
                {
                    ...KEYS,
                    projectNumberLabelText() {
                        return {
                            key: KEYS.projectNumberLabel,
                            values: { projectNumber: this.Project.ProjectNumber }
                        };
                    },
                    printHeader() {
                        return {
                            key: KEYS.printHeaderText,
                            values: {
                                title: this.Form.Title,
                                user: this.userLabel,
                                time: this.currentTime,
                                date: this.currentDate
                            }
                        };
                    },
                    autoPromotionMessage() {
                        return {
                            key: KEYS.autoPromotionMessage,
                            values: { formVersionDescription: this.Form.Description }
                        };
                    }
                },
                NAMESPACE
            ),
            ...mapI18n(COMMON_KEYS, COMMON_NAMESPACE)
        },
        watch: {
            async forceOnePage() {
                // updates displayed page to first page when pagination is toggled on currentPageNumber > 1
                this.setPage(1);

                // reload to rerender the displayed page on forms using batch draw
                await this.beginReload();
                this.reloading = false;
                this.loading = false;
            }
        },
        async created() {
            // If the ProjectForm module is already registered, component was probably
            // destroyed and recreated without page reloading , so do abbreviated re-initialization.
            // No need to re-register any modules since store should still exist.
            if (isModuleRegistered(PROJECT_FORM_NAMESPACE, this.$store.state)) {
                this._initWatchers();
                this.loading = false;
            }
            const loadPromise = this.baseController.wistiaLoadPromise || Promise.resolve();
            await loadPromise;
        },
        unmounted() {
            // Unhook stale watchers in case we recreate the component without loading a new
            // page.
            this._unhookWatchers();
        },
        methods: {
            pageAtIndex(index) {
                return this.Pages[index];
            },
            pageClicked(page) {
                this.setPage(page.PageNumber);
            },
            previousPage() {
                this.setPage(this.currentPageNumber - 1);
            },
            nextPage() {
                this.setPage(this.currentPageNumber + 1);
            },
            setPage(pageNumber) {
                if (pageNumber >= 1 && pageNumber <= this.Pages.length) {
                    //reset this on every page move to prevent the submit button showing up and then going away
                    this.showSubmitOnEPaymentPage = false;
                    this.pageSlideLeft = this.currentPageNumber < pageNumber;
                    this.currentPageNumber = pageNumber;
                    if (!this.isAdmin) {
                        this.$store.dispatch(PROJECT_FORM_CONTROLLER_TYPES.actions.saveFormData, null, { root: true });
                    }
                }
            },
            async setElement(elementId) {
                this.setPage(this.elementIdToPageNumber[elementId]);
                await this.$nextTick();
                await this.pageTransitionPromise;
                scrollToElement(`element${elementId}`);
            },
            async submit() {
                if (!this.showSubmit) {
                    return;
                }
                if (this.isPreview) {
                    window.close();
                    return;
                }
                if (this.beforeSubmit) {
                    try {
                        await this.beforeSubmit();
                    } catch (error) {
                        return;
                    }
                }
                this.$app.blockUi(this.processingSubmitMessage);
                try {
                    this.clearValidationErrors();
                    const submitResult = await this.$store.dispatch(
                        PROJECT_FORM_CONTROLLER_TYPES.actions.submitProject,
                        null,
                        { root: true }
                    );
                    if (submitResult.HasError) {
                        await this.$app.raiseResponseError(submitResult.UserReadableError);
                    } else if (submitResult.ValidationErrors && submitResult.ValidationErrors.length > 0) {
                        this.setValidationErrors(submitResult.ValidationErrors);
                    } else {
                        //submit successful
                        this.emitSubmitSuccess(submitResult);
                    }
                } finally {
                    this.$app.unBlockUi();
                }
            },
            emitSubmitSuccess(submitResult) {
                this.$emit("submit-success", submitResult);
            },
            clearValidationErrors() {
                this.$store.commit(VALIDATION_ERRORS_MUTATION, null, { root: true });
            },
            setValidationErrors(validationErrors) {
                this.$store.commit(VALIDATION_ERRORS_MUTATION, validationErrors, { root: true });
            },
            pageTransitionStart() {
                this.pageTransitionEnd();
                let resolve = null;
                const promise = new Promise((_resolve) => {
                    resolve = _resolve;
                });
                promise._pf_resolve = resolve;
                this.pageTransitionPromise = promise;
            },
            pageTransitionEnd() {
                if (this.pageTransitionPromise && this.pageTransitionPromise._pf_resolve) {
                    this.pageTransitionPromise._pf_resolve();
                }
            },
            getValidationErrorsOfType(errorType) {
                if (this.validationErrors && this.validationErrors.length > 0) {
                    const errors = this.validationErrors.filter((ve) => ve.Type === errorType);
                    if (errors.length > 0) {
                        return errors;
                    }
                }
                return null;
            },
            initialize({ Project, Form, ProjectForm, User, Program }) {
                if (IS_DEBUG_BUILD) {
                    if (!isModuleRegistered(PROJECT_FORM_CONTROLLER_NAMESPACE, this.$store.state)) {
                        console.error(
                            "project controller store module is missing." +
                                "Make sure to register a store module with the namespace " +
                                `[${PROJECT_FORM_CONTROLLER_NAMESPACE}].`
                        );
                    }
                }

                this._initProjectElementAuxState(Project, Form, ProjectForm);

                const projectModule = createProjectModule(Project);
                const formModule = createFormModule(Form);
                const userModule = createUserModule(User);
                const programModule = createProgramModule(this.$app, Program);
                const projectFormModule = createProjectFormModule(this.$app, this.baseController, ProjectForm);

                this.$store.registerModule(PROJECT_NAMESPACE, projectModule);
                this.$store.registerModule(FORM_NAMESPACE, formModule);
                this.$store.registerModule(USER_NAMESPACE, userModule);
                this.$store.registerModule(PROGRAM_NAMESPACE, programModule);
                this.$store.registerModule(PROJECT_FORM_NAMESPACE, projectFormModule);

                this._initWatchers();
                this._initSummationFields();
                this._setInitialPage();
                this.loading = false;
            },
            /**
             * Call this when dynamically loading a new form without refreshing the
             * page and creating a new projectForm from scratch
             */
            async beginReload() {
                // Trying to get loader to appear before destroy so it at least doesn't look frozen.
                // Once we set loading = true, IE may freeze up because of the elements it has to destroy
                this.reloading = true;
                await this.$app.timeout(0);

                this.loading = true;
            },
            // Reload all state specific to a given form
            reinitialize({ Project, Form, ProjectForm }) {
                this.$store.unregisterModule(FORM_NAMESPACE);
                this.$store.unregisterModule(PROJECT_FORM_NAMESPACE);
                this.$store.unregisterModule(PROJECT_NAMESPACE);
                this._unhookWatchers();

                this._initProjectElementAuxState(Project, Form, ProjectForm);

                const projectModule = createProjectModule(Project);
                const formModule = createFormModule(Form);
                const projectFormModule = createProjectFormModule(this.$app, this.baseController, ProjectForm);

                this.$store.registerModule(PROJECT_NAMESPACE, projectModule);
                this.$store.registerModule(FORM_NAMESPACE, formModule);
                this.$store.registerModule(PROJECT_FORM_NAMESPACE, projectFormModule);

                this._initWatchers();
                this._initSummationFields();
                this._setInitialPage();
                this.reloading = false;
                this.loading = false;
            },
            _initSummationFields() {
                const elements = this.$store.getters[FORM_TYPES.getters.orderedFormElements];
                const summationElements = elements.filter((e) => e.ClassName === "SummationElement");
                summationElements.forEach((element) => {
                    onDependencyUpdate({ element, rootGetters: this.$store.getters, dispatch: this.$store.dispatch });
                });
            },
            _initProjectElementAuxState(Project, Form, ProjectForm) {
                for (const publicId in Form.FormElements) {
                    if (Form.FormElements.hasOwnProperty(publicId)) {
                        const element = Form.FormElements[publicId];
                        const projectElement = ProjectForm.ProjectElements[publicId];
                        const auxState = getAuxProjectElementState(element, projectElement, Project);
                        projectElement.auxState = auxState;
                    }
                }
            },
            _setInitialPage() {
                let pageNumber = 1;
                if (this.projectFormMode === PROJECT_FORM_MODE.Edit && !this.Form.AlwaysOpenToFirstPage) {
                    if (!this.projectIsNew) {
                        //don't include the ePayment page in the index calculation
                        const pagesSansEPayment = this.showEPaymentPage ? this.Pages.slice(0, -1) : this.Pages.slice(0);
                        const pageIndex = pagesSansEPayment.findIndex((p) => !isPageComplete(p, this.$store.getters));
                        if (pageIndex > 0) {
                            pageNumber = pageIndex + 1;
                        }
                    }
                }
                // If project locked for payment, go straight to the ePayments page to show
                // that a payment is underway
                if (this.projectLockStore.lockedForPendingPayment) {
                    pageNumber = this.Pages.length;
                }
                if (pageNumber !== this.currentPageNumber) {
                    this.setPage(pageNumber);
                }
            },
            _initWatchers() {
                this.calculatedFieldController_init();
                this.visibilityManager_init();
                if (this.pvStorageVisibility) {
                    this.pvStorageVisibility.setupWatchers(this.$serviceControllers);
                }
            },
            _unhookWatchers() {
                if (this.pvStorageVisibility) {
                    this.pvStorageVisibility.unhook();
                }
                this.visibilityManager_unhook();
                this.calculatedFieldController_unhook();
            }
        }
    };
</script>
<style lang="scss" scoped>
    .form-pagination-arrow {
        display: inline-block;
        width: 5%;
        align-self: center;
    }

    .form-pagination-arrow:hover {
        cursor: pointer;
    }
</style>
